import React from 'react';
import FormattedNumber from 'components/FormattedNumber';
import CropIconMapping from 'components/CropIconMapping';
import FieldIcon from 'components/Icons/FieldIcon';
import HighlightedText from 'components/HighlightedText';
import { TREE_PADDING, COLORS, ICON_SIZES } from '../../constants';
import {
  getArchivedTextColor,
  getNonArchivedTextColor,
  getArchivedFieldIconColor,
  getNonArchivedFieldIconColor
} from '../../helpers';
import ArchivedLabel from '../ArchivedLabel';

interface CropZoneTreeItemProps {
  name: string;
  archivedState: {
    isArchived: boolean;
    showLabel?: boolean;
  };
  appliedArea: number;
  cropName?: string;
  geometry?: any;
  paddingClass?: string;
  searchTerm?: string;
}

const CropZoneTreeItem: React.FC<CropZoneTreeItemProps> = ({
  name,
  archivedState,
  appliedArea,
  cropName,
  geometry,
  paddingClass = TREE_PADDING.CROP_ZONE,
  searchTerm = ''
}) => {
  const { isArchived, showLabel: showArchivedLabel } = archivedState;

  // Crop zone shape: light fill + field-color border so it reads as an outlined
  // shape (distinct from a field's solid fill). Archived uses a muted pair.
  const iconFill = isArchived
    ? COLORS.CROP_ZONE_ICON_FILL_ARCHIVED
    : COLORS.CROP_ZONE_ICON_FILL;
  const iconBorder = isArchived
    ? getArchivedFieldIconColor()
    : getNonArchivedFieldIconColor();

  return (
    <div
      className={`w-full py-3 ${paddingClass} pr-1 text-left flex items-center justify-between`}
    >
      <div
        className="flex items-start overflow-hidden"
        style={{ maxWidth: '80%' }}
      >
        <FieldIcon
          id={`cropzone-icon-${name}`}
          features={
            geometry
              ? [
                  {
                    type: 'Feature',
                    geometry,
                    properties: {}
                  }
                ]
              : []
          }
          width={ICON_SIZES.FIELD}
          height={ICON_SIZES.FIELD}
          color={iconFill}
          border={iconBorder}
        />
        <div className="ml-2 overflow-hidden">
          <div className="flex items-center">
            <HighlightedText
              text={name}
              searchTerm={searchTerm}
              className="font-medium leading-5 truncate"
              style={{
                marginRight: showArchivedLabel ? '4px' : '0',
                color: isArchived ? COLORS.ARCHIVED_TEXT : undefined
              }}
            />
            {showArchivedLabel && <ArchivedLabel />}
          </div>
          <div className="mt-2px text-xs leading-3 flex items-center">
            <span
              style={{
                color: isArchived
                  ? getArchivedTextColor()
                  : getNonArchivedTextColor()
              }}
            >
              <FormattedNumber roundedTo={2}>{appliedArea}</FormattedNumber> ac
              ·{' '}
            </span>
            {cropName && (
              <>
                <div
                  style={{
                    flexShrink: 0,
                    marginLeft: '4px',
                    marginRight: '4px'
                  }}
                >
                  <CropIconMapping
                    cropObject={{ name: cropName }}
                    size={ICON_SIZES.CROP}
                    color={isArchived ? COLORS.ARCHIVED_TEXT : undefined}
                  />
                </div>
                <HighlightedText
                  text={cropName}
                  searchTerm={searchTerm}
                  style={{
                    color: isArchived
                      ? getArchivedTextColor()
                      : getNonArchivedTextColor()
                  }}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CropZoneTreeItem;
